import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generateCardData, DASHBOARD } from "../../constants/dashboardConstants";
import DashboardCard from "./DashboardCard";
import DashboardUpcomingTrip from "./DashboardUpcomingTrip.jsx";
import useFleetOverviewLegacy from "../../hooks/useFleetOverviewLegacy";
import { useUser } from "../../pages/login/UserContextPersistent";

const cardPermissionMap = {
  vehicles: { path: "/api/vehicle", action: "GET" },
  routes: { path: "/api/route", action: "GET" },
  stops: { path: "/api/stop", action: "GET" },
  alerts: { path: "/api/alert", action: "GET" },
  drivers: { path: "/api/driver", action: "GET" },
  sites: { path: "/api/site", action: "GET" },
};

export default function FleetDashboardLegacyView({ organizationId, userFeatures, onUnauthorized }) {
  const { t, i18n } = useTranslation();
  const { organizationInactive } = useUser();
  const { overview, loading } = useFleetOverviewLegacy(organizationId, onUnauthorized);
  const [cardKeyMap, setCardKeyMap] = useState({});

  useEffect(() => {
    if (!Object.keys(overview).length) return;
    const englishCardData = generateCardData(overview, (key) => key);
    const newCardKeyMap = {};
    englishCardData.forEach((card) => {
      for (const [permType] of Object.entries(cardPermissionMap)) {
        if (card.title.toLowerCase().includes(permType.toLowerCase())) {
          newCardKeyMap[card.id] = permType;
          break;
        }
      }
    });
    setCardKeyMap(newCardKeyMap);
  }, [overview]);

  const hasPermission = (requiredPath, requiredAction = null) => {
    if (!userFeatures || !requiredPath) return true;
    return userFeatures.some((feature) => {
      const pathMatches = feature.api_path?.includes(requiredPath);
      return requiredAction ? pathMatches && feature.action === requiredAction : pathMatches;
    });
  };

  const filteredCards = useMemo(() => {
    const cardData = generateCardData(overview, t);
    return cardData.filter((card) => {
      const permissionType = cardKeyMap[card.id];
      if (!permissionType) return true;
      const permission = cardPermissionMap[permissionType];
      if (!permission) return true;
      return hasPermission(permission.path, permission.action);
    });
  }, [overview, t, i18n.language, userFeatures, cardKeyMap]);

  if (loading) return null;

  return (
    <>
      <span className="dashboard-title">{t(DASHBOARD)}</span>
      <div className="row">
        {filteredCards.length > 0 ? (
          <>
            <div
              className={`dashboard-grid-container${organizationInactive ? " dashboard-grid-container--full" : ""}`}
            >
              {filteredCards.map((card) => (
                <DashboardCard key={card.id} {...card} />
              ))}
            </div>
            {!organizationInactive && (
              <div className="dashboard-upcoming-section">
                <DashboardUpcomingTrip />
              </div>
            )}
          </>
        ) : (
          <div className="no-permissions-state">
            <p>{t("noDataFound")}</p>
          </div>
        )}
      </div>
    </>
  );
}
